import { EncounterDetails, IssuesResponseType } from "entities/issues";
import { InfoTypes, SinglePatientInfo } from "entities/patientInformation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { EncounterWidth, TileYearWidth } from "utils/constants";
import { ExtendedInfoType } from "../components/types";
import { VitalsChartData } from "./types";
import dayjs from "dayjs";
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrBefore);


export default function useMonthTimeline(
  patientInformations?: SinglePatientInfo[],
  issues?: IssuesResponseType
) {
  const [allMonths, setAllMonths] = useState<string[]>([]);
  const [maxEncounterWidth, setMaxEncounterWidth] = useState(0);

  const latestYear = useMemo(() => {
    const date = new Date();
    let year = date.getFullYear();

    issues?.encounters?.forEach((data) => {
      const encounterYear = data?.date?.split("-")[0];

      if (encounterYear) {
        year = Math.max(year, +encounterYear);
      }
    });

    return year + 1;
  }, [issues?.encounters]);

  const getMonthsBetween = useCallback(
    (start: string, end: string): string[] => {
      const startDate = dayjs(start);
      const endDate = dayjs(end);
      const months = [];

      let currentDate = startDate.clone();

      while (currentDate.isSameOrBefore(endDate, "month")) {
        const formattedMonth = currentDate.format("YYYY-MM");
        months.push(formattedMonth);
        currentDate = currentDate.add(1, "month");
      }
      return months;
    },
    []
  );

  const groupPatientData = useMemo(() => {
    const groupedData: Record<InfoTypes, ExtendedInfoType[]> = {
      allergy: [],
      dental: [],
      medical_problem: [],
      medication: [],
      surgery: [],
      tests: [],
      vitals: [],
      medical_device: [],
    };
    const date = new Date();
    const dateStringParts = date.toISOString().split("-");
    const year = latestYear;
    const yearAndMonth = `${latestYear}-${dateStringParts[1]}`;

    const newMonths = new Set(allMonths);

    if (patientInformations) {
      patientInformations.forEach((info) => {
        const beginingDate = +info?.begdate.split("-")[0];
        const endDate = info.enddate ? +info.enddate.split("-")[0] : year; //eg 2024

        let endYearMonth = info.enddate
          ? `${info.enddate.split("-")[0]}-${info.enddate.split("-")[1]}`
          : yearAndMonth; //eg 2024-04

        if (
          info.type === "surgery" ||
          info.type === "allergy" ||
          info.type === "medical_device" ||
          info.type === "dental"
        ) {
          endYearMonth = yearAndMonth;
        }

        const startYearMonth = info.begdate
          ? `${info.begdate.split("-")[0]}-${info.begdate.split("-")[1]}`
          : yearAndMonth; //eg 2024-04

        newMonths.add(startYearMonth);
        newMonths.add(endYearMonth);
        const months = getMonthsBetween(startYearMonth, endYearMonth);

        groupedData[info.type].push({
          ...info,
          enddate: info.enddate ?? date.toISOString(),
          tileWidth: (endDate - beginingDate) * TileYearWidth,
          end: endYearMonth,
          start: startYearMonth,
          contigiousDates: months,
        });
      });
    }

    setAllMonths(Array.from(newMonths).sort());

    return groupedData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientInformations, latestYear, getMonthsBetween]);

  const monthEncounters = useMemo(() => {
    const encounters: Record<string, EncounterDetails[]> = {};
    const vitalChartData: VitalsChartData = {};
    allMonths.forEach((month) => {
      encounters[month.toString()] = [];
      vitalChartData[month.toString()] = [];
    });

    if (issues) {
      issues.encounters.forEach((encounter) => {
        const encounterDateParts = encounter.date.split("-");
        const encounterMonth = `${encounterDateParts[0]}-${encounterDateParts[1]}`;
        if (encounterMonth !== "0000-00") {
          if (encounters[encounterMonth]) {
            encounters[encounterMonth] = [
              ...encounters[encounterMonth],
              ...encounter.encounters,
            ];
          } else {
            encounters[encounterMonth] = [...encounter.encounters];
          }

          encounter.encounters.forEach((data) => {
            data.vitals.forEach((vitals) => {
              if (vitalChartData[encounterMonth]) {
                vitalChartData[encounterMonth].push({
                  bps: vitals.bps,
                  bpd: vitals.bpd,
                  pulse: vitals.pulse,
                  temperature: vitals.temperature,
                  height: vitals.height,
                  respiratoryRate: vitals.respiration,
                  weight: vitals.weight,
                  bmi: vitals.BMI,
                  date: vitals.date,
                });
              } else {
                vitalChartData[encounterMonth] = [
                  {
                    bps: vitals.bps,
                    bpd: vitals.bpd,
                    pulse: vitals.pulse,
                    temperature: vitals.temperature,
                    height: vitals.height,
                    respiratoryRate: vitals.respiration,
                    weight: vitals.weight,
                    bmi: vitals.BMI,
                    date: vitals.date,
                  },
                ];
              }
            });
          });
        }
      });
    }
    return { encounters, vitalChartData };
  }, [issues, allMonths]);

  useEffect(() => {
    const monthEncounterKeys = Object.keys(monthEncounters.encounters);
    let width = 0;
    for (const month of monthEncounterKeys) {
      if (monthEncounters.encounters[month as InfoTypes]) {
        width +=
          monthEncounters.encounters[month as InfoTypes].length *
            EncounterWidth +
          400;
      } else {
        width += TileYearWidth;
      }
    }
    setMaxEncounterWidth(width);
  }, [monthEncounters.encounters]);

  return {
    months: Object.keys(monthEncounters.encounters).sort(),
    monthGroupPatientData: groupPatientData,
    monthEncounters: monthEncounters.encounters,
    vitalsChartData: monthEncounters.vitalChartData,
    maxEncounterWidth,
  };
}
